import { Box, Button, Container, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useLocale, useTranslations } from 'next-intl'
import React from 'react'

import { CountryWithMCC } from '@/src/api/rtkQuery/countryApi'
import GoogleSignInButton from '@/src/components/common/GoogleSignInButton'
import { OtpStep } from '@/src/components/common/header/variants/flyin/SignInModal/otpVerificationModal'
import GoogleIcon from '@/src/components/common/header/variants/flyin/SignInModal/otpVerificationModal/GoogleIcon'
import ItineraryAutoComplete from '@/src/components/common/itinerary/auto-complete'
import { UITreatment } from '@/src/services/otpErrorMapping'
import { GoogleUserData } from '@/src/types/auth'
import { maskEmail } from '@/src/utils/auth'

import { OTP_PAGE_BENEFITS } from './constants'

const OtpInput = MuiOtpInput as any

// Shared TextField styling for disabled Google user fields
const disabledTextFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: 36,
        fontSize: 14,
        bgcolor: 'grey.100',
        '& fieldset': {
            borderColor: 'grey.300',
            borderRadius: '4px'
        }
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 12px',
        fontSize: 14
    }
}

interface FlowData {
    currentStep: OtpStep
    error: string
    errorState: {
        message: string
        uiTreatment?: UITreatment
        field?: 'email' | 'mobile' | 'countryCode' | 'otp'
    }
    isWelcomeBack: boolean
    existingEmail: string | null
    isGoogleFlow: boolean
    googleUserData: GoogleUserData | null

    // Mobile data
    mobileCountryCode: string
    selectedCountryCode: CountryWithMCC | null
    countryData: CountryWithMCC[] | undefined
    isCountryDataLoading: boolean
    mobileNumber: string
    mobileOtp: string

    // Email data
    email: string
    emailOtp: string

    // Loading states
    isSendingOtp: boolean
    isVerifyingOtp: boolean

    // Timer states
    mobileResendTimer: number
    emailResendTimer: number
}

interface FlowHandlers {
    onClose: () => void

    // Mobile handlers
    handleCountryCodeChange: (country: CountryWithMCC | null) => void
    handleMobileNumberChange: (value: string) => void
    handleMobileOtpChange: (value: string) => void
    handleSendMobileOtp: () => void
    handleVerifyMobileOtp: () => void
    handleResendMobileOtp: () => void
    handleBackToMobileInput: () => void

    // Email handlers
    handleEmailChange: (value: string) => void
    handleEmailOtpChange: (value: string) => void
    handleSendEmailOtp: () => void
    handleVerifyEmailOtp: () => void
    handleResendEmailOtp: () => void
    handleBackToEmailInput: () => void

    // Google Sign-In handlers
    handleGoogleSignIn: (userData: GoogleUserData) => void
    handleGoogleSignInError: () => void
    handleSendGoogleMobileOtp: () => void
    handleVerifyGoogleMobileOtp: () => void
    handleResendGoogleMobileOtp: () => void
}

interface OtpPageContentProps {
    flowData: FlowData
    flowHandlers: FlowHandlers
}

const OtpPageContent: React.FC<OtpPageContentProps> = ({ flowData, flowHandlers }) => {
    const {
        currentStep,
        error,
        errorState,
        isWelcomeBack,
        existingEmail,
        isGoogleFlow,
        googleUserData,
        mobileCountryCode,
        selectedCountryCode,
        countryData,
        isCountryDataLoading,
        mobileNumber,
        mobileOtp,
        email,
        emailOtp,
        isSendingOtp,
        isVerifyingOtp,
        mobileResendTimer,
        emailResendTimer
    } = flowData

    const {
        handleCountryCodeChange,
        handleMobileNumberChange,
        handleMobileOtpChange,
        handleSendMobileOtp,
        handleVerifyMobileOtp,
        handleResendMobileOtp,
        handleBackToMobileInput,
        handleEmailChange,
        handleEmailOtpChange,
        handleSendEmailOtp,
        handleVerifyEmailOtp,
        handleResendEmailOtp,
        handleBackToEmailInput,
        handleGoogleSignIn,
        handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp
    } = flowHandlers

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const locale = useLocale()
    const t = useTranslations('otpModal')

    const renderMobileInputStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 22, lg: 26 }, mb: 1 }}>
                    {t('enterMobileNumber')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }}>
                    {t('mobileVerificationDescription')}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        width: 100,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 12
                        }
                    }}
                >
                    <ItineraryAutoComplete
                        label=""
                        disableClearable
                        loading={isCountryDataLoading}
                        options={countryData || []}
                        value={selectedCountryCode as any}
                        onChange={(value: any) => handleCountryCodeChange(value as CountryWithMCC | null)}
                        getOptionLabel={(option: any) => {
                            const country = option as CountryWithMCC
                            return `+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`
                        }}
                        renderValueForMobileScreen={(value: any) => {
                            const country = value as CountryWithMCC
                            return `+ ${country?.mobileCountryCode}`
                        }}
                        renderOption={(props: any, option: any) => {
                            const country = option as CountryWithMCC
                            return (
                                <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                                    {`+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`}
                                </Typography>
                            )
                        }}
                    />
                </Box>

                <TextField
                    placeholder={t('enterMobileNumber')}
                    type="tel"
                    value={mobileNumber}
                    onChange={e => handleMobileNumberChange(e.target.value)}
                    fullWidth
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 14,
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '4px'
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                            fontSize: 14
                        }
                    }}
                />
            </Box>

            {error && <Typography sx={{ color: 'error.main', fontSize: 12 }}>{error}</Typography>}

            <Button
                fullWidth
                variant="customVariantPrimary"
                color="primary"
                onClick={handleSendMobileOtp}
                disabled={isSendingOtp}
                sx={{
                    height: 36,
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: '4px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none'
                    }
                }}
            >
                {isSendingOtp ? t('sendingOtp') : t('sendOtp')}
            </Button>

            {/* Google Sign-In Button */}
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: '1px',
                        bgcolor: 'grey.300'
                    }}
                />
                <Typography
                    sx={{
                        position: 'relative',
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        display: 'inline-block',
                        px: 2,
                        fontSize: 12,
                        color: 'text.secondary',
                        left: '50%',
                        transform: 'translateX(-50%)'
                    }}
                >
                    {t('or')}
                </Typography>
            </Box>

            <GoogleSignInButton
                onSuccess={handleGoogleSignIn}
                onError={() => {
                    flowHandlers.handleGoogleSignInError?.()
                }}
                buttonText={t('continueWithGoogle')}
                fullWidth
                sx={{
                    height: 36,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                        backgroundColor: 'grey.50',
                        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
                    }
                }}
                startIcon={<GoogleIcon style={{ width: '18px', height: '18px' }} />}
            />
        </Box>
    )

    const renderMobileVerificationStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 }, mb: 1 }}>
                    {t('verifyMobileNumber')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }}>
                    {t('mobileOtpDescription', { mobileCountryCode: `+${mobileCountryCode}`, mobileNumber })}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <OtpInput
                    value={mobileOtp}
                    onChange={(value: string) => {
                        // Filter out non-numeric characters
                        const numericValue = value.replace(/\D/g, '')
                        handleMobileOtpChange(numericValue)
                    }}
                    onComplete={(value: string) => {
                        if (value.length === 4) {
                            isGoogleFlow ? handleVerifyGoogleMobileOtp() : handleVerifyMobileOtp()
                        }
                    }}
                    length={4}
                    autoFocus
                    validateChar={(char: string) => /^\d$/.test(char)}
                    TextFieldsProps={{
                        size: 'small',
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            width: { xs: 45, sm: 50 },
                            height: { xs: 45, sm: 50 },
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '8px',
                                borderWidth: 2
                            },
                            '&:hover fieldset': {
                                borderColor: 'primary.main'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: 2
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            textAlign: 'center',
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600
                        }
                    }}
                />
            </Box>

            {error && <Typography sx={{ color: 'error.main', fontSize: 12, textAlign: 'center' }}>{error}</Typography>}

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleBackToMobileInput}
                    sx={{
                        flex: 1,
                        height: 36,
                        fontSize: 14,
                        borderRadius: '4px',
                        textTransform: 'none'
                    }}
                >
                    {t('back')}
                </Button>
                <Button
                    variant="customVariantPrimary"
                    onClick={isGoogleFlow ? handleVerifyGoogleMobileOtp : handleVerifyMobileOtp}
                    disabled={isVerifyingOtp || mobileOtp.length !== 4}
                    sx={{
                        flex: 1,
                        height: 36,
                        fontSize: 14,
                        fontWeight: 600,
                        borderRadius: '4px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isVerifyingOtp ? t('verifyingOtp') : t('verifyOtp')}
                </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>{t('didntReceiveOtp')}</Typography>
                <Button
                    variant="text"
                    onClick={isGoogleFlow ? handleResendGoogleMobileOtp : handleResendMobileOtp}
                    disabled={mobileResendTimer > 0}
                    sx={{
                        fontSize: 12,
                        textTransform: 'none',
                        color: 'primary.main',
                        minWidth: 'auto',
                        p: 0.5
                    }}
                >
                    {mobileResendTimer > 0 ? t('resendOtpIn', { seconds: mobileResendTimer }) : t('resendOtp')}
                </Button>
            </Box>
        </Box>
    )

    const renderEmailInputStep = () => {
        // Create masked email for display if existingEmail exists
        const maskedEmail = existingEmail ? maskEmail(existingEmail) : null

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 }, mb: 1 }}>
                        {t('enterEmailAddress')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }}>
                        {isWelcomeBack && maskedEmail
                            ? t('welcomeBackMessage', { email: maskedEmail })
                            : !existingEmail
                                ? t('enterEmailToComplete')
                                : t('emailVerificationDescription')}
                    </Typography>
                </Box>

                <TextField
                    placeholder={t('emailAddress')}
                    type="email"
                    value={email}
                    onChange={e => handleEmailChange(e.target.value)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 14,
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '4px'
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                            fontSize: 14
                        }
                    }}
                />

                {error && <Typography sx={{ color: 'error.main', fontSize: 12 }}>{error}</Typography>}

                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleSendEmailOtp}
                    disabled={isSendingOtp}
                    sx={{
                        height: 36,
                        fontSize: 14,
                        fontWeight: 600,
                        borderRadius: '4px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isSendingOtp ? t('sendingOtp') : t('sendOtp')}
                </Button>
            </Box>
        )
    }

    const renderEmailVerificationStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 }, mb: 1 }}>
                    {t('verifyEmailAddress')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }}>
                    {t('emailOtpDescription', { email })}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <OtpInput
                    value={emailOtp}
                    onChange={(value: string) => {
                        // Filter out non-numeric characters
                        const numericValue = value.replace(/\D/g, '')
                        handleEmailOtpChange(numericValue)
                    }}
                    onComplete={(value: string) => {
                        if (value.length === 4) {
                            handleVerifyEmailOtp()
                        }
                    }}
                    length={4}
                    autoFocus
                    validateChar={(char: string) => /^\d$/.test(char)}
                    TextFieldsProps={{
                        size: 'small',
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            width: { xs: 45, sm: 50 },
                            height: { xs: 45, sm: 50 },
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '8px',
                                borderWidth: 2
                            },
                            '&:hover fieldset': {
                                borderColor: 'primary.main'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: 2
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            textAlign: 'center',
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600
                        }
                    }}
                />
            </Box>

            {error && <Typography sx={{ color: 'error.main', fontSize: 12, textAlign: 'center' }}>{error}</Typography>}

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleBackToEmailInput}
                    sx={{
                        flex: 1,
                        height: 36,
                        fontSize: 14,
                        borderRadius: '4px',
                        textTransform: 'none'
                    }}
                >
                    {t('back')}
                </Button>
                <Button
                    variant="customVariantPrimary"
                    onClick={handleVerifyEmailOtp}
                    disabled={isVerifyingOtp || emailOtp.length !== 4}
                    sx={{
                        flex: 1,
                        height: 36,
                        fontSize: 14,
                        fontWeight: 600,
                        borderRadius: '4px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isVerifyingOtp ? t('verifyingOtp') : t('verifyOtp')}
                </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>{t('didntReceiveOtp')}</Typography>
                <Button
                    variant="text"
                    onClick={handleResendEmailOtp}
                    disabled={emailResendTimer > 0}
                    sx={{
                        fontSize: 12,
                        textTransform: 'none',
                        color: 'primary.main',
                        minWidth: 'auto',
                        p: 0.5
                    }}
                >
                    {emailResendTimer > 0 ? t('resendOtpIn', { seconds: emailResendTimer }) : t('resendOtp')}
                </Button>
            </Box>
        </Box>
    )

    // Shared component for Google user fields
    const renderGoogleUserFields = () => (
        <>
            {/* First Name and Last Name - 50/50 row layout */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                    label={t('firstName')}
                    value={googleUserData?.firstName || 'N/A'}
                    disabled
                    sx={{ ...disabledTextFieldSx, flex: 1 }}
                />

                <TextField
                    label={t('lastName')}
                    value={googleUserData?.lastName || 'N/A'}
                    disabled
                    sx={{ ...disabledTextFieldSx, flex: 1 }}
                />
            </Box>

            {/* Email - Pre-filled and Disabled */}
            <TextField label={t('email')} value={email} disabled fullWidth sx={disabledTextFieldSx} />
        </>
    )

    const renderGoogleMobileInputStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 22, lg: 26 }, mb: 1 }}>
                    {t('completeRegistration')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }}>
                    {t('provideMobileNumberDescription')}
                </Typography>
            </Box>

            {renderGoogleUserFields()}

            {/* Mobile number input fields */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        width: 100,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 12
                        }
                    }}
                >
                    <ItineraryAutoComplete
                        label=""
                        disableClearable
                        loading={isCountryDataLoading}
                        options={countryData || []}
                        value={selectedCountryCode as any}
                        onChange={(value: any) => handleCountryCodeChange(value as CountryWithMCC | null)}
                        getOptionLabel={(option: any) => {
                            const country = option as CountryWithMCC
                            return `+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`
                        }}
                        renderValueForMobileScreen={(value: any) => {
                            const country = value as CountryWithMCC
                            return `+ ${country?.mobileCountryCode}`
                        }}
                        renderOption={(props: any, option: any) => {
                            const country = option as CountryWithMCC
                            return (
                                <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                                    {`+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`}
                                </Typography>
                            )
                        }}
                    />
                </Box>

                <TextField
                    placeholder={t('enterMobileNumber')}
                    type="tel"
                    value={mobileNumber}
                    onChange={e => handleMobileNumberChange(e.target.value)}
                    fullWidth
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 14,
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '4px'
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                            fontSize: 14
                        }
                    }}
                />
            </Box>

            {error && <Typography sx={{ color: 'error.main', fontSize: 12 }}>{error}</Typography>}

            <Button
                fullWidth
                variant="customVariantPrimary"
                color="primary"
                onClick={handleSendGoogleMobileOtp}
                disabled={isSendingOtp}
                sx={{
                    height: 36,
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: '4px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none'
                    }
                }}
            >
                {isSendingOtp ? t('sendingOtp') : t('sendOtp')}
            </Button>
        </Box>
    )

    const renderCurrentStep = () => {
        switch (currentStep) {
            case OtpStep.MOBILE_INPUT:
                return renderMobileInputStep()
            case OtpStep.MOBILE_VERIFICATION:
                return renderMobileVerificationStep()
            case OtpStep.EMAIL_INPUT:
                return renderEmailInputStep()
            case OtpStep.EMAIL_VERIFICATION:
                return renderEmailVerificationStep()
            case OtpStep.GOOGLE_MOBILE_INPUT:
                return renderGoogleMobileInputStep()
            default:
                return renderMobileInputStep()
        }
    }

    return (
        <Box sx={{ minHeight: { xs: '70vh', md: '80vh' }, bgcolor: 'grey.50' }}>
            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 6 } }}>
                {/* Page Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            fontSize: { sm: 24, md: 30, lg: 34 },
                            mb: 2,
                            color: 'primary.main'
                        }}
                    >
                        {t('signInBenefitsTitle')}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: { sm: 14, md: 16, lg: 18 },
                            color: 'text.secondary',
                            maxWidth: 800,
                            mx: 'auto',
                            lineHeight: 1.6
                        }}
                    >
                        {t('signInBenefitsDescription')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: { xs: 3, lg: 4 },
                        maxWidth: 1000,
                        mx: 'auto'
                    }}
                >
                    {/* Left: OTP Form Section */}
                    <Box
                        sx={{
                            flex: 1,
                            bgcolor: 'background.paper',
                            borderRadius: { xs: 2, md: 3 },
                            p: { xs: 3, sm: 4, md: 5 },
                            boxShadow: { xs: 1, md: 3 },
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}
                    >
                        {/* Form Content */}
                        <Box sx={{ maxWidth: 500, mx: 'auto' }}>{renderCurrentStep()}</Box>

                        {/* Terms and Privacy */}
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                                {t('termsAndPrivacy')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: Benefits Section - Hidden on mobile */}
                    <Box
                        sx={{
                            width: { xs: '100%', lg: 400 },
                            bgcolor: 'primary.main',
                            background: theme =>
                                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            color: 'common.white',
                            borderRadius: { xs: 2, md: 3 },
                            p: { xs: 3, sm: 4, md: 5 },
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: { xs: 'auto', lg: 500 }
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, fontSize: 22 }}>
                            {t('registerWithFlyin')}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, fontSize: 13, opacity: 0.9 }}>
                            {t('getFollowingBenefits')}
                        </Typography>

                        {/* Desktop: Vertical list with text */}
                        <Box
                            component="ul"
                            sx={{
                                pl: 0,
                                m: 0,
                                listStyle: 'none',
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            {OTP_PAGE_BENEFITS.map((benefit, index) => (
                                <Box
                                    component="li"
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 2,
                                        '&:last-child': { mb: 0 }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            flexShrink: 0
                                        }}
                                    >
                                        {benefit.icon}
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{t(benefit.textKey)}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Mobile: Horizontal icons only */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2,
                                mt: 2
                            }}
                        >
                            {OTP_PAGE_BENEFITS.map((benefit, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                                            transform: 'scale(1.05)'
                                        }
                                    }}
                                    title={t(benefit.textKey)}
                                >
                                    {benefit.icon}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { OtpPageContent }
export type { OtpPageContentProps }
